import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import {
    Button,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    FormControlLabel,
    Grid,
    Menu,
    MenuItem,
    Switch,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    Tabs,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { get, includes, orderBy } from "lodash";
import React, { useEffect, useState } from "react";
import ReactGA from "react-ga4";
import { Link } from "react-router-dom";
import BSInput from "../../../common/js/components/BSInput";
import CustomDataGridFilterToolbar from "../../../common/js/components/CustomDataGridFilterToolbar";
import Snackbar from "../../../common/js/components/KMSnackbar";
import StatusChip from "../../../common/js/components/StatusChip";
import { ExportIcon, List, ListingCircle, Map, Search, Sorty } from "../../../common/js/icons";
import NameSearchModal from "../search/NameSearchModal";
import SavedSearchesModal from "../search/SavedSearchesModal";
import { navigate } from "../utils/navigate";
import { aggregateBrokersByPersonObjectForTooltip, buildAddress, toDollarAmount, toSqft } from "../utils/operations";
import BrokerRequestedListingTable from "./BrokerRequestedListingsTable";
import ListingFilters from "./ListingFilters";
import ListingImportModal from "./ListingImportModal";
import "./Listings.css";
import { filterColumns } from "./ListingsDataGridColumnDefinitions";
import ListingsWithMap from "./ListingsWithMap";
import ListingsWithoutMap from "./ListingsWithoutMap";
import ReportTitleModal from "./ReportTitleModal";
import schema, { buttonActions } from "./reviewListingsTables";

const StyledPageToolbar = styled("div")(() => ({
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: "1vw",
    marginBottom: "20px",
    marginTop: "20px",
}));

const StyledTabOne = styled(Tab)(() => ({
    marginRight: "20px",
}));

const StyledMenuSubItem = styled(MenuItem)({
    "&>a": {
        paddingLeft: "30px !important",
    },
});

const StyledAlternateContent = styled("div")({
    margin: "auto",
    textAlign: "center",
    marginTop: "6em",
    marginBottom: "6em",
    color: "rgba(0, 0, 0, 0.30)",
    fontSize: "20px",
});

const StyledLink = styled(Link)({
    textDecoration: "none",
});

const StyledToplessInput = styled(BSInput)({
    marginTop: 0,
});

const StyledSortable = styled("span")({
    paddingRight: "5px",
    paddingBottom: "3px",
});

const StyledListingIdLink = styled("span")({
    color: "black",
    fontWeight: 600,
});

const StyledListingNumber = styled("h4")({
    fontSize: "14px",
    margin: "0",
    borderColor: "#f1f1f1",
    color: "#2C99D2",
    fontWeight: 600,
    "&:hover": {
        cursor: "pointer",
        textDecoration: "underline",
    },
});

const StyledLoadingProgress = styled(CircularProgress)({
    marginRight: "5px",
    color: "grey",
});

const StyledDataGridFilter = styled("div")({
    display: "flex",
    padding: "8px 0",
});

const StyledGroupSwitchDiv = styled("div")({
    display: "flex",
    justifyContent: "flex-end",
});

const StyledDataGridContainerDiv = styled("div")({
    display: "flex",
    flexDirection: "column",
});

const ReviewListings = (props) => {
    const {
        addSelectedToGroup,
        clearCurrentGroup,
        clearFilters,
        closeDeal,
        columnToSort,
        dataExport,
        deleteSelectedFromGroup,
        elementsSaving,
        errors,
        filterQuery,
        getListingsByStatusSortAndGrouping,
        handleDataExport,
        handleDeleteCrumb,
        handleFilter,
        handleListingsSearchSubmit,
        handleListingTypeRadioClick,
        handleSearchCleared,
        handleSelectAll,
        handleSelectAllGroup,
        handleSelectAllPropertyType,
        handleSelectMarket,
        handleSelectMicromarket,
        handleSelectSubmarket,
        handleSizeRadioClick,
        handleSort,
        handleStatusRadioClick,
        isLoading,
        listingCurrentGroup,
        listings,
        listingTab,
        loadedSearchCrumbs,
        marketFilterExpansionPanels,
        nameSaveModal,
        numListings,
        propertySearchFilters,
        propertyTypeExpansionPanels,
        role,
        searchCleared,
        searchCriteria,
        searchPolygon,
        searchResponse,
        searchRun,
        selectedListings,
        selectedListingsGroup,
        setListingsFilter,
        sortDirection,
        switchTab,
        toggleMarketFilterExpansionPanel,
        togglePropertyTypeExpansionPanel,
        toggleReportTitleModal,
        updateFilter,
        updateSearchAreaFilter,
        updateSearchPolygon,
        user,
        viewSavedModal,
        checkIfListingIsAlreadyAssigned,
    } = props;

    const [selectAll, setSelectAll] = useState(true);
    const [selectAllGroup, setSelectAllGroup] = useState(true);
    const [mapDisplay, setMapDisplay] = useState(true);
    const [reportDropdown, setReportDropdown] = useState(null);
    const [reportStyle, setReportStyle] = useState(null);
    const [exportDropdown, setExportDropdown] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState("");
    const [snackbarVariant, setSnackbarVariant] = useState("");
    const [closeDealDialogOpen, setCloseDealDialogOpen] = useState(false);
    const [listingToClose, setListingToClose] = useState(false);
    const [isGrouped, setIsGrouped] = useState(true);

    useEffect(() => {
        ReactGA.send({ hitType: "pageview", page: "/listings", title: "Admin Listings" });
    }, []);

    const handleSelectAllLocal = () => {
        const checked = selectAll;
        handleSelectAll(!checked);
        setSelectAll(!checked);
    };

    const handleSelectAllGroupLocal = () => {
        const checked = selectAllGroup;
        handleSelectAllGroup(!checked);
        setSelectAllGroup(!checked);
    };

    const handleCloseAndStart = async () => {
        const result = await closeDeal(listingToClose);
        if (result) {
            if (result.dealKey) {
                navigate(`/deal/${result.dealKey}`);
            }
        }
        setCloseDealDialogOpen(false);
        setListingToClose(null);
    };

    const handleClose = () => {
        setCloseDealDialogOpen(false);
        setListingToClose(null);
    };

    const toggleMap = (mapView) => {
        setMapDisplay(mapView);
    };

    const handleSelectReport = (reportStyle) => {
        setReportDropdown(null);
        setReportStyle(reportStyle);
        toggleReportTitleModal(true);
    };

    const handleReportDropdown = (e) => {
        setReportDropdown(e.currentTarget);
    };

    const handleExportDropdown = (e) => {
        setExportDropdown(e.currentTarget);
    };
    const handleExportAndClose = (selectionType, activeTab) => {
        setExportDropdown(null);
        handleDataExport(selectionType, activeTab);
    };
    const handleOpenModal = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    const handleImportResponse = (result) => {
        if (result === true) {
            setSnackbarMessage("Success: Your file will now be processed.");
            setSnackbarVariant("success");
            setSnackbarOpen(true);
        } else if (result === false) {
            setSnackbarMessage("Failed: Columns must match Standard Import Template.");
            setSnackbarVariant("error");
            setSnackbarOpen(true);
        }
    };

    const listingCount = numListings && !elementsSaving[listingTab] ? numListings[listingTab] : 0;
    const listingz = listings[listingTab] ? listings[listingTab] : {};
    const tab = includes(["data_team_unassigned", "data_team_open", "all_km_listings", "listings_group"], listingTab)
        ? listingTab
        : "data_team_unassigned";
    let disableDeleteFromGroup = true;
    const disclaimerText = `© 2019 - ${new Date().getFullYear()} Kidder Mathews Inc and its subsidiaries and/or assigns. The information supplied herein is from sources we deem reliable. It is provided without any representation, warranty, or guarantee, expressed or implied as to its accuracy. Prospective Buyer or Tenant should conduct an independent investigation and verification of all matters deemed to be material, including, but not limited to, statements of income and expenses. Consult your attorney, accountant, or other professional advisor. All information is proprietary to Kidder Mathews and intended for the sole purpose of providing consultancy services to clients and should not be reproduced, distributed or used for any other purpose nor divulged to third party data collection agencies as outlined by corporate policy.`;

    //sets disableDeleteFromGroup to false for Delete from Group button if no listings are selected
    let list = [];
    for (var [key, value] of selectedListingsGroup) {
        if (value && !list.includes(key)) {
            list.push(key);
        }
    }
    if (list.length !== 0) {
        disableDeleteFromGroup = false;
    }

    return (
        <div>
            <Collapse in={listingTab === "all_km_listings"} unmountOnExit style={{ marginLeft: -20, marginRight: -20 }}>
                <ListingFilters
                    role={role}
                    updateFilter={updateFilter}
                    searchSubmit={handleListingsSearchSubmit}
                    propertySearchFilters={propertySearchFilters}
                    handleSelectAllPropertyType={handleSelectAllPropertyType}
                    clearFilters={clearFilters}
                    searchCriteria={searchCriteria}
                    handleStatusRadioClick={handleStatusRadioClick}
                    handleSizeRadioClick={handleSizeRadioClick}
                    handleListingTypeRadioClick={handleListingTypeRadioClick}
                    errors={errors}
                    togglePropertyTypeExpansionPanel={togglePropertyTypeExpansionPanel}
                    propertyTypeExpansionPanels={propertyTypeExpansionPanels}
                    toggleMarketFilterExpansionPanel={toggleMarketFilterExpansionPanel}
                    marketFilterExpansionPanels={marketFilterExpansionPanels}
                    handleSelectMarket={handleSelectMarket}
                    handleSelectSubmarket={handleSelectSubmarket}
                    handleSelectMicromarket={handleSelectMicromarket}
                    handleSearchCleared={handleSearchCleared}
                    loadedSearchCrumbs={loadedSearchCrumbs}
                    handleDeleteCrumb={handleDeleteCrumb}
                    searchCleared={searchCleared}
                />
            </Collapse>
            <Snackbar
                open={snackbarOpen}
                message={snackbarMessage}
                variant={snackbarVariant}
                vertical="top"
                horizontal="center"
                autoHideDuration={3000}
                onClose={() => {
                    setSnackbarOpen(false);
                }}
            />
            <StyledPageToolbar>
                <div>
                    <h2 className="page-title">Listings</h2>
                    <div className="page-subtitle">
                        {listingTab !== "all_km_listings" &&
                            listingTab !== "listings_group" &&
                            `Showing ${listingCount} ${listingCount === 1 ? "listing" : "listings"} `}
                        {filterQuery > "" && listingTab !== "all_km_listings" && "(Filtered)"}
                        {listingTab === "all_km_listings" &&
                            searchResponse &&
                            searchResponse.results &&
                            `Showing  ${searchResponse.results.length} ${
                                searchResponse.results.length === 1
                                    ? "listing"
                                    : `listings (${searchResponse.totalResultCount} TOTAL)`
                            }`}

                        {listingTab === "listings_group" &&
                            listingCurrentGroup &&
                            `You have ${listingCurrentGroup.length} ${
                                listingCurrentGroup.length === 1
                                    ? "listing in your current list"
                                    : `listings in your current list`
                            }`}
                    </div>
                </div>
                <div style={{ display: "flex" }}>
                    {listingTab === "all_km_listings" ? (
                        <Button
                            disabled={false}
                            variant="contained"
                            color="primary"
                            onClick={(e) => addSelectedToGroup()}
                        >
                            Add to List
                        </Button>
                    ) : listingTab === "listings_group" ? (
                        <React.Fragment>
                            <Button
                                disabled={disableDeleteFromGroup}
                                variant="contained"
                                color="primary"
                                onClick={(e) => deleteSelectedFromGroup()}
                            >
                                Delete From List
                            </Button>
                            <Button
                                disabled={disableDeleteFromGroup}
                                variant="contained"
                                color="primary"
                                onClick={(e) => clearCurrentGroup()}
                            >
                                Clear List
                            </Button>
                        </React.Fragment>
                    ) : (
                        ""
                    )}

                    {listingTab === "all_km_listings" &&
                    includes(["dataAdmin", "dataEntry", "research", "researchAdmin"], role) ? (
                        <React.Fragment>
                            <Button
                                disabled={false}
                                variant="contained"
                                color="primary"
                                onClick={() => handleOpenModal()}
                            >
                                Import
                            </Button>
                            <ListingImportModal
                                modalOpen={modalOpen}
                                closeModal={handleCloseModal}
                                importResponse={handleImportResponse}
                            />
                        </React.Fragment>
                    ) : (
                        ""
                    )}

                    {(listingTab === "all_km_listings" || listingTab === "listings_group") &&
                        (get(dataExport, "exportInProgress", false) === true ? (
                            <Button disabled={true} variant="outlined" color="primary">
                                <ExportIcon /> In Progress
                            </Button>
                        ) : (
                            <Tooltip
                                title={disclaimerText}
                                placement="left-start"
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: "offset",
                                                options: {
                                                    offset: [0, 75],
                                                },
                                            },
                                        ],
                                    },
                                }}
                            >
                                <div>
                                    <Button
                                        id="Export_Listing"
                                        aria-haspopup="true"
                                        onClick={(e) => handleExportDropdown(e)}
                                        endIcon={<ArrowDropDownIcon />}
                                        color="primary"
                                        variant="contained"
                                        disabled={Object.keys(listingz).length === 0}
                                    >
                                        Export
                                    </Button>
                                    {exportDropdown && (
                                        <React.Fragment>
                                            <Menu
                                                id="Export_Listing_Menu"
                                                anchorEl={exportDropdown}
                                                open={Boolean(exportDropdown)}
                                                onClose={() => {
                                                    setExportDropdown(null);
                                                }}
                                                anchorOrigin={{
                                                    vertical: "bottom",
                                                    horizontal: "right",
                                                }}
                                                transformOrigin={{
                                                    vertical: "top",
                                                    horizontal: "right",
                                                }}
                                            >
                                                <MenuItem onClick={() => handleExportAndClose("selected", listingTab)}>
                                                    Selected Listings Only
                                                </MenuItem>
                                                <MenuItem onClick={() => handleExportAndClose("all", listingTab)}>
                                                    {listingTab === "listings_group"
                                                        ? "Entire Group"
                                                        : "Entire Data Set"}
                                                </MenuItem>
                                            </Menu>
                                        </React.Fragment>
                                    )}
                                </div>
                            </Tooltip>
                        ))}

                    {(listingTab === "all_km_listings" || listingTab === "listings_group") &&
                        includes(["dataAdmin", "dataEntry"], role) &&
                        (elementsSaving.generateListingReport ? (
                            <Button disabled={true} variant="contained" color="secondary">
                                <StyledLoadingProgress size={15} />
                                Generating
                            </Button>
                        ) : (
                            <Tooltip
                                title={disclaimerText}
                                placement="left-start"
                                slotProps={{
                                    popper: {
                                        modifiers: [
                                            {
                                                name: "offset",
                                                options: {
                                                    offset: [0, 75],
                                                },
                                            },
                                        ],
                                    },
                                }}
                            >
                                <div>
                                    <Button
                                        id="Generate_Listing_Report"
                                        aria-haspopup="true"
                                        onClick={(e) => handleReportDropdown(e)}
                                        endIcon={<ArrowDropDownIcon />}
                                        color="primary"
                                        variant="contained"
                                        disabled={Object.keys(listingz).length === 0}
                                    >
                                        Generate Report
                                    </Button>
                                    {reportDropdown && (
                                        <Menu
                                            id="Generate_Listing"
                                            anchorEl={reportDropdown}
                                            open={Boolean(reportDropdown)}
                                            onClose={() => {
                                                setReportDropdown(null);
                                            }}
                                            anchorOrigin={{
                                                vertical: "bottom",
                                                horizontal: "right",
                                            }}
                                            transformOrigin={{
                                                vertical: "top",
                                                horizontal: "right",
                                            }}
                                        >
                                            <StyledMenuSubItem onClick={() => handleSelectReport("Standard")}>
                                                Standard
                                            </StyledMenuSubItem>
                                            <StyledMenuSubItem onClick={() => handleSelectReport("MapOverview")}>
                                                Map Overview
                                            </StyledMenuSubItem>
                                            <StyledMenuSubItem
                                                onClick={() => handleSelectReport("HalfMap-HalfProperty")}
                                            >
                                                Half Map / Half Property List
                                            </StyledMenuSubItem>
                                            <StyledMenuSubItem onClick={() => handleSelectReport("IndividualDetail")}>
                                                Individual Listing Page Detail
                                            </StyledMenuSubItem>
                                            <StyledMenuSubItem onClick={() => handleSelectReport("Reference")}>
                                                Property Reference
                                            </StyledMenuSubItem>
                                        </Menu>
                                    )}
                                </div>
                            </Tooltip>
                        ))}
                </div>
            </StyledPageToolbar>
            <div style={{ display: "flex" }}>
                <div className="simPaper" style={{ flex: "1 1 auto", minWidth: "500px", overflow: "hidden" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", flexWrap: "wrap" }}>
                        <div>
                            <Tabs value={tab} onChange={(event, value) => switchTab(event, value, user)}>
                                <StyledTabOne value="data_team_unassigned" label="Unassigned Listings" />
                                <Tab value="data_team_open" label="Open Listings" />
                                <Tab value="all_km_listings" label="All Listings" />
                                <Tab
                                    value="listings_group"
                                    label={`Current List (${listingCurrentGroup ? listingCurrentGroup.length : "0"})`}
                                />
                            </Tabs>
                        </div>
                        {listingTab === "data_team_unassigned" && (
                            <StyledDataGridFilter>
                                <CustomDataGridFilterToolbar
                                    key={listingTab}
                                    fields={filterColumns[listingTab]}
                                    setFilter={setListingsFilter}
                                    setPage={() => {}}
                                    onSubmit={() => getListingsByStatusSortAndGrouping(listingTab, isGrouped)}
                                />
                            </StyledDataGridFilter>
                        )}
                        {listingTab !== "all_km_listings" &&
                            listingTab !== "listings_group" &&
                            listingTab !== "data_team_unassigned" && (
                                <div>
                                    <StyledToplessInput
                                        value={filterQuery}
                                        placeholder="Search listings"
                                        errorPlaceholder="hideIt"
                                        startAdornment={<Search />}
                                        onChange={(event) => {
                                            handleFilter(event.target.value);
                                        }}
                                    />
                                </div>
                            )}

                        {(listingTab === "all_km_listings" || listingTab === "listings_group") && (
                            <div>
                                <Grid container>
                                    {mapDisplay ? (
                                        <Grid>
                                            <Button style={{ minWidth: "40px" }} onClick={() => toggleMap(false)}>
                                                <List />
                                            </Button>
                                        </Grid>
                                    ) : (
                                        <Grid>
                                            <Button style={{ minWidth: "40px" }} onClick={() => toggleMap(true)}>
                                                <Map />
                                            </Button>
                                        </Grid>
                                    )}
                                </Grid>
                            </div>
                        )}
                    </div>
                    {listingTab === "all_km_listings" || listingTab === "listings_group" ? (
                        mapDisplay ? (
                            <ListingsWithMap
                                {...props}
                                selectAll={selectAll}
                                selectAllGroup={selectAllGroup}
                                searchRun={searchRun}
                                searchCleared={listingTab === "listings_group" ? false : searchCleared}
                                handleSelectAll={handleSelectAllLocal}
                                handleSelectAllGroup={handleSelectAllGroupLocal}
                                updateSearchAreaFilter={updateSearchAreaFilter}
                                selectedListings={selectedListings}
                                listingz={listingz}
                                handleSearchCleared={handleSearchCleared}
                                searchPolygon={searchPolygon}
                                updateSearchPolygon={updateSearchPolygon}
                            />
                        ) : (
                            <ListingsWithoutMap
                                {...props}
                                selectAll={selectAll}
                                selectAllGroup={selectAllGroup}
                                searchRun={searchRun}
                                searchCleared={listingTab === "listings_group" ? false : searchCleared}
                                handleSelectAll={handleSelectAllLocal}
                                handleSelectAllGroup={handleSelectAllGroupLocal}
                                selectedListings={selectedListings}
                                listingz={listingz}
                                handleSearchCleared={handleSearchCleared}
                            />
                        )
                    ) : (
                        ""
                    )}

                    {listingTab === "data_team_unassigned" && (
                        <StyledDataGridContainerDiv>
                            <StyledGroupSwitchDiv>
                                <FormControlLabel
                                    control={
                                        <Switch checked={isGrouped} onChange={(e) => setIsGrouped(e.target.checked)} />
                                    }
                                    label={isGrouped ? "Grouped" : "Ungrouped"}
                                    disabled={elementsSaving[listingTab]}
                                />
                            </StyledGroupSwitchDiv>
                            <BrokerRequestedListingTable {...props} isGrouped={isGrouped} />
                        </StyledDataGridContainerDiv>
                    )}

                    {listingTab !== "all_km_listings" &&
                        listingTab !== "listings_group" &&
                        listingTab !== "data_team_unassigned" && (
                            <div style={{ display: "flex", overflowX: "scroll" }}>
                                {elementsSaving[listingTab] ? (
                                    <StyledAlternateContent>
                                        <CircularProgress color="primary" />
                                    </StyledAlternateContent>
                                ) : (
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                {schema.viewHeaders[listingTab].map((x, i) => (
                                                    <TableCell key={`thc-${i}`}>
                                                        <div
                                                            style={{
                                                                display: "flex",
                                                                alignItems: "center",
                                                            }}
                                                            onClick={() => handleSort(x.sort ? x.sort : x.prop)}
                                                        >
                                                            <StyledSortable>{x.name}</StyledSortable>
                                                            {x.name && (
                                                                <div>
                                                                    <Sorty />
                                                                </div>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                ))}
                                                <TableCell></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {Object.keys(listingz).map((propKey, i) =>
                                                row(
                                                    propKey,
                                                    role,
                                                    listingTab,
                                                    isLoading,
                                                    columnToSort,
                                                    sortDirection,
                                                    listingz,
                                                    filterQuery,
                                                    checkIfListingIsAlreadyAssigned,
                                                    navigate
                                                )
                                            )}
                                        </TableBody>
                                    </Table>
                                )}
                            </div>
                        )}
                    {listingTab === "data_team_open" &&
                    !elementsSaving[listingTab] &&
                    Object.keys(listingz).length === 0 ? (
                        <StyledAlternateContent>No listings</StyledAlternateContent>
                    ) : null}
                </div>
            </div>
            <Dialog
                open={closeDealDialogOpen}
                onClose={handleClose}
                aria-labelledby="close-deal"
                aria-describedby="close-deal-dialog"
            >
                <DialogTitle id="close-deal">{"Are you sure?"}</DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        Closing this deal will start the invoice process for this transaction and move the listing to a
                        closed status. Portland Listings will be closed after the Comp for this Deal has been Submitted
                        and Approved.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} color="primary" variant="outlined">
                        Cancel
                    </Button>
                    <Button onClick={handleCloseAndStart} color="secondary" autoFocus variant="contained">
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
            <ReportTitleModal {...props} reportType="Listing" tab={listingTab} reportStyle={reportStyle} />
            <SavedSearchesModal {...props} searchType="listings" tab={listingTab} showModal={viewSavedModal} />
            <NameSearchModal {...props} searchType="listings" tab={listingTab} showModal={nameSaveModal} />
        </div>
    );
};

const row = (
    propKey,
    role,
    listingTab,
    isLoading,
    columnToSort,
    sortDirection,
    listingz,
    filterQuery,
    checkIfListingIsAlreadyAssigned,
    navigate
) => {
    const sortedData = orderBy(
        filterQuery && listingz && listingz[propKey]
            ? listingz[propKey].filter((x) => schema.filterByQuery(x, schema.columnsToQueryFilter, filterQuery))
            : listingz[propKey],
        columnToSort,
        sortDirection
    );

    const handleStart = async (listingKey) => {
        const result = await checkIfListingIsAlreadyAssigned(listingKey);

        if (result && !result.success) {
            setTimeout(function () {
                window.location.reload();
            }, 1500);
        } else {
            navigate(`/listing/${listingKey}?action=start`);
        }
    };

    return isLoading ? null : (
        <React.Fragment>
            {sortedData.length > 0 && (
                <TableRow>
                    <TableCell colSpan={8}>
                        <div style={{ display: "flex", alignItems: "center", gap: "1vw" }}>
                            <div>
                                <ListingCircle />
                            </div>
                            <div>
                                <div className="cluster">
                                    <span className="clusterHeader">
                                        {get(sortedData[0], "property.propertyAttributes.propertyName", null)
                                            ? get(sortedData[0], "property.propertyAttributes.propertyName", "")
                                            : get(sortedData[0], "property.primaryAddress.address1", "")}
                                    </span>
                                    <span className="clusterSubHeader">
                                        {`${sortedData.length} ${sortedData.length === 1 ? "listing" : "listings"}`}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </TableCell>
                </TableRow>
            )}

            {sortedData.map((x, i) => {
                const buttonProperties = get(buttonActions, `${role}.${x.status}`, buttonActions.defaultAction);
                return (
                    <TableRow key={`tr-${i}`} selectable="false">
                        {schema.viewHeaders[listingTab].map((y, k) =>
                            y.name === "Address" ? (
                                <TableCell key={`trc-${k}`}>
                                    <StyledLink to={`/listing/${x.listingKey}`}>
                                        <StyledListingNumber>{`No. ${get(x, "listingKey", "")}`}</StyledListingNumber>
                                        <StyledListingIdLink>{get(x, "listingName", "")}</StyledListingIdLink>
                                    </StyledLink>
                                    <div>{buildAddress(get(x, "property.primaryAddress", {}))}</div>
                                </TableCell>
                            ) : y.name === "Brokers" ? (
                                <TableCell key={`trc-${k}`}>
                                    <Tooltip
                                        id={`review-listing-broker-tooltip-${k}`}
                                        placement="top"
                                        title={aggregateBrokersByPersonObjectForTooltip(
                                            get(x, y.prop),
                                            "brokerAgentKey.person"
                                        )}
                                    >
                                        <span data-tooltip-id={`review-listing-broker-tooltip-${k}`}>
                                            {y.transform(get(x, y.prop))}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            ) : y.transform ? (
                                <TableCell key={`trc-${k}`} align={y.align}>
                                    {y.transform(get(x, y.prop))}
                                </TableCell>
                            ) : y.prop === "status" && buttonProperties ? (
                                <TableCell>
                                    {/* Status */}
                                    <StatusChip
                                        status={
                                            buttonProperties.statusToDisplay && buttonProperties.statusToDisplay !== ""
                                                ? buttonProperties.statusToDisplay
                                                : x.status
                                        }
                                    />
                                </TableCell>
                            ) : y.prop === "sfAvail" ? (
                                <TableCell>
                                    {/* Rate */}
                                    {get(x, "listingType", null) === "Lease"
                                        ? get(x, "sfDivisibleTo", null)
                                            ? `${toSqft(get(x, "sfDivisibleTo"))} - ${toSqft(get(x, "sfAvail"))}`
                                            : `${toSqft(get(x, "sfAvail", "-"))}`
                                        : `${toSqft(get(x, "sfAvail", "-"))}`}
                                </TableCell>
                            ) : y.name === "Rate" ? (
                                <TableCell>
                                    {/* Rate */}
                                    {get(x, "listingType", null) === "Lease"
                                        ? `${toDollarAmount(get(x, "askingRentMax"))}`
                                        : `${toDollarAmount(get(x, "listPrice", "-"))}`}
                                </TableCell>
                            ) : (
                                <TableCell key={`trc-${k}`} align={y.align}>
                                    {/* Type */}
                                    {get(x, y.prop)}
                                </TableCell>
                            )
                        )}

                        <TableCell align="right">
                            {buttonProperties.urlParam === "start" ? (
                                <Button
                                    variant={buttonProperties.variant}
                                    color="primary"
                                    onClick={() => handleStart(x.listingKey)}
                                >
                                    {buttonProperties.text}
                                </Button>
                            ) : (
                                <StyledLink
                                    to={
                                        buttonProperties.urlParam
                                            ? `/listing/${x.listingKey}?action=${buttonProperties.urlParam}`
                                            : `/listing/${x.listingKey}`
                                    }
                                >
                                    <Button variant={buttonProperties.variant} color="primary" onClick={() => null}>
                                        {buttonProperties.text}
                                    </Button>
                                </StyledLink>
                            )}
                        </TableCell>
                    </TableRow>
                );
            })}
        </React.Fragment>
    );
};

export default ReviewListings;
